<script lang="ts">
  // Body host: an open shadow root that carries the reader stylesheet (spec §10.1) and the
  // sanitised html (or the html built from plain text). Quotes are collapsed on render; the
  // parent unblocks images / reads the rendered text through the exported functions.
  import { untrack } from 'svelte';
  import { t } from '$lib/i18n';
  import { renderIntoShadow, renderedText, unblockImages } from '$lib/mail/sanitize';

  let {
    html,
    isHtml,
    variant = 'reader',
    onrendered,
  }: {
    /** Already sanitised html (or `textToHtml()` output). */
    html: string;
    /** `true` puts the body on the white "paper" (`.tik-paper`). */
    isHtml: boolean;
    /** `reader` → `.body`, `thread` → `.tbody` (the ThreadItem mini card). */
    variant?: 'reader' | 'thread';
    /** Called after every render with the shadow root (auto-unblock for trusted senders). */
    onrendered?: (root: ShadowRoot) => void;
  } = $props();

  let host = $state<HTMLDivElement | null>(null);
  let root: ShadowRoot | null = null;

  $effect(() => {
    const el = host;
    const content = html;
    const paper = isHtml;
    if (!el) return;
    root = renderIntoShadow(el, content, paper, { show: t('showQuoted'), hide: t('hideQuoted') }, root);
    const r = root;
    untrack(() => onrendered?.(r));
  });

  /** Restore blocked remote images/styles; returns how many images were unblocked. */
  export function unblock(): number {
    return root ? unblockImages(root) : 0;
  }

  /** `innerText` of the rendered body (for quoting in replies). */
  export function text(): string {
    return renderedText(root);
  }

  export function shadow(): ShadowRoot | null {
    return root;
  }
</script>

<div class={variant === 'thread' ? 'tbody' : 'body'} bind:this={host}></div>

<style>
  /* svelte-rwh28b (Reader) */
  .body {
    line-height: var(--leading-body)
  }

  @media (pointer:fine) {
    .body {
      scrollbar-gutter: stable
    }
  }

  /* svelte-10er1on (ThreadItem) */
  .tbody {
    padding: 0 var(--space-4) var(--space-4)
  }
</style>
